import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertManagementPanel = ({ alerts, batches, onRefresh }) => {
  const [filterPriority, setFilterPriority] = useState('all');
  const [escalationView, setEscalationView] = useState(false);

  const getAlertConfig = (type) => {
    const configs = {
      error: { bgColor: 'bg-error/10', borderColor: 'border-error/30', textColor: 'text-error', icon: 'AlertCircle' },
      warning: { bgColor: 'bg-warning/10', borderColor: 'border-warning/30', textColor: 'text-warning', icon: 'AlertTriangle' },
      info: { bgColor: 'bg-primary/10', borderColor: 'border-primary/30', textColor: 'text-primary', icon: 'Info' }
    };
    return configs?.[type] || configs?.info;
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      urgent: { label: 'URGENT', color: 'bg-error text-white' },
      high: { label: 'HAUTE', color: 'bg-orange-500 text-white' },
      medium: { label: 'MOYENNE', color: 'bg-warning text-white' },
      low: { label: 'BASSE', color: 'bg-gray-400 text-white' }
    };
    return badges?.[priority] || badges?.medium;
  };

  const filteredAlerts = filterPriority === 'all'
    ? alerts
    : alerts?.filter(a => a?.priority === filterPriority);

  const urgentAlerts = alerts?.filter(a => a?.priority === 'urgent');
  const highAlerts = alerts?.filter(a => a?.priority === 'high');

  const formatTime = (timestamp) => {
    const now = new Date();
    const diff = Math.floor((now - timestamp) / 1000);
    
    if (diff < 60) return 'À l\'instant';
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} h`;
    return `Il y a ${Math.floor(diff / 86400)} j`;
  };

  const handleEscalate = (alert) => {
    // Escalation workflow logic
    alert(`Escalade de l'alerte ${alert?.batchNumber} au superviseur`);
  };

  return (
    <div className="space-y-6">
      {/* Alert Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
              <Icon name="AlertCircle" size={24} className="text-error" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{urgentAlerts?.length}</div>
              <div className="text-sm text-gray-600">Urgentes</div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
              <Icon name="AlertTriangle" size={24} className="text-warning" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{highAlerts?.length}</div>
              <div className="text-sm text-gray-600">Priorité Haute</div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Clock" size={24} className="text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {alerts?.filter(a => a?.title?.includes('24h'))?.length}
              </div>
              <div className="text-sm text-gray-600">Délai 24h</div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
              <Icon name="Calendar" size={24} className="text-orange-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {alerts?.filter(a => a?.title?.includes('10'))?.length}
              </div>
              <div className="text-sm text-gray-600">Délai 10j</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter & View Toggle */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {['all', 'urgent', 'high', 'medium', 'low']?.map(priority => (
              <button
                key={priority}
                onClick={() => setFilterPriority(priority)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterPriority === priority
                    ? 'bg-primary text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {priority === 'all' ? 'Toutes' : getPriorityBadge(priority)?.label}
              </button>
            ))}
          </div>
          <Button
            variant={escalationView ? 'primary' : 'outline'}
            iconName="ArrowUpCircle"
            onClick={() => setEscalationView(!escalationView)}
          >
            Vue Escalade
          </Button>
        </div>
      </div>

      {/* Alert List */}
      {filteredAlerts?.length > 0 ? (
        <div className="space-y-3">
          {filteredAlerts?.map(alert => {
            const config = getAlertConfig(alert?.type);
            const priorityBadge = getPriorityBadge(alert?.priority);

            return (
              <div
                key={alert?.id}
                className={`${config?.bgColor} border ${config?.borderColor} rounded-lg p-5`}
              >
                <div className="flex items-start gap-4">
                  <Icon name={config?.icon} size={24} className={config?.textColor} />
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <h4 className={`text-base font-semibold ${config?.textColor}`}>{alert?.title}</h4>
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${priorityBadge?.color}`}>
                          {priorityBadge?.label}
                        </span>
                      </div>
                      <span className="text-xs text-gray-600">{formatTime(alert?.timestamp)}</span>
                    </div>
                    <p className="text-sm text-gray-900 mb-3">{alert?.message}</p>
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-medium text-gray-700">Lot: {alert?.batchNumber}</span>
                      {escalationView && alert?.escalation && (
                        <span className="text-xs px-2 py-1 rounded bg-gray-900 text-white">
                          Escalade: {alert?.escalation}
                        </span>
                      )}
                    </div>
                    
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <Icon name="CheckCircle" size={64} className="mx-auto text-success mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune alerte</h3>
          <p className="text-sm text-gray-600">Tous les lots sont dans les délais</p>
        </div>
      )}
    </div>
  );
};

export default AlertManagementPanel;